import type { PayloadAction, UnknownAction } from '@reduxjs/toolkit';

import { router } from '@/routes/appRoutes';
import type { IncomingMessageOfType } from '@/store/listeners/poc/incomingMessageMatchers';
import { facilitateFileKeys } from '@/store/thunks/fileKey/facilitateFileKeysThunk';
import { refreshSixCatalogThunk } from '@/store/thunks/sixCatalog/refreshSixCatalog';
import { isIncomingMessageOfType } from '@/store/listeners/poc/incomingMessageMatchers';
import type { AppStartListening } from '@/store/listeners/types';
import { initializeP2PStackThunk } from '@/store/thunks/poc/connection/initializeP2PStackThunk';
import { reconcileDeletedFilesThunk } from '@/store/thunks/poc/cleanup/reconcileDeletedFilesThunk';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';

const REFRESH_DEBOUNCE_MS = 50;

// initializeP2PStackThunk fires once per SIX (the catalog fans it out), but the
// login-time cleanup should run only once per session — the first stack-init that
// brings the Helia node up. Reset on logout so the next login reconciles again.
let hasReconciledDeletedFilesThisSession = false;

export function registerIncomingMaintenanceListeners(startAppListening: AppStartListening): void {
  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'refreshSixes'>> =>
      isIncomingMessageOfType(action, 'refreshSixes'),
    effect: async (_action, listenerApi) => {
      if (router.state.location.pathname !== '/app/sixes') return;
      listenerApi.cancelActiveListeners();
      await listenerApi.delay(REFRESH_DEBOUNCE_MS);
      void listenerApi.dispatch(refreshSixCatalogThunk());
      void listenerApi.dispatch(facilitateFileKeys());
    },
  });

  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'refreshFileKeys'>> =>
      isIncomingMessageOfType(action, 'refreshFileKeys'),
    effect: (_action, listenerApi) => {
      void listenerApi.dispatch(facilitateFileKeys());
    },
  });

  // On login: once the P2P stack is up, purge locally-held blocks the server has
  // marked deleted for this user. Guarded to run a single time per session.
  startAppListening({
    actionCreator: initializeP2PStackThunk.fulfilled,
    effect: (_action, listenerApi) => {
      if (hasReconciledDeletedFilesThisSession) return;
      hasReconciledDeletedFilesThisSession = true;
      void listenerApi.dispatch(reconcileDeletedFilesThunk('login'));
    },
  });

  startAppListening({
    actionCreator: logoutThunk.pending,
    effect: () => {
      hasReconciledDeletedFilesThisSession = false;
    },
  });

  // On the backend-emitted refresh signal: re-run the same purge unconditionally.
  startAppListening({
    matcher: (
      action: UnknownAction
    ): action is PayloadAction<IncomingMessageOfType<'refreshFilesToDeleteList'>> =>
      isIncomingMessageOfType(action, 'refreshFilesToDeleteList'),
    effect: (_action, listenerApi) => {
      void listenerApi.dispatch(reconcileDeletedFilesThunk('refreshMessage'));
    },
  });
}
